import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Box, Flex, Text } from '@radix-ui/themes';
import ReactCalendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './style.scss';

import { instance } from 'api/axios.instance';
import { localList } from 'app/constants';
import { selectDevice } from 'store/slices/common';
import iconHouse from 'assets/images/icon/icon-house.svg';
import iconBook from 'assets/images/icon/icon-book.svg';
import iconParty from 'assets/images/icon/icon-party.svg';
import iconArrowPrev from 'assets/images/icon/icon-arrow-prev.svg';
import iconArrowNext from 'assets/images/icon/icon-arrow-next.svg';
import iconClose from 'assets/images/icon/icon-menu-close.svg';
import imgEmptyCalendarPc from 'assets/images/pages/main/img-empty-calendar.png';
import imgEmptyCalendarMo from 'assets/images/pages/main/icon-empty-calendar.png';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const EVENT_TYPE_LABEL = {
  mirae: '미래채움',
  edu: '교육',
  event: '행사'
};

const formatDay = (locale, d) => d.getDate();
const formatShortWeekday = (locale, d) => WEEKDAYS[d.getDay()];

const getTileClassName = ({ date: tileDate, view }) => {
  if (view !== 'month') return null;
  if (tileDate.getDay() === 0) return 'sunday';
  if (tileDate.getDay() === 6) return 'saturday';
  return null;
};

// /post/list 호출에 사용할 게시판 (boardType 파라미터)
const CALENDAR_BOARD_TYPES = ['education', 'event'];

// API 응답 board(한글) → 캘린더 일정 타입 매핑
// 미래채움(mirae)은 대응되는 게시판이 없어 현재 매핑하지 않음
const BOARD_TYPE_MAP = {
  교육정보: 'edu',
  행사정보: 'event'
};

// 일정 타입 → 상세 페이지 경로
const DETAIL_PATH_MAP = {
  edu: 'education',
  event: 'event'
};

const getEventDetailPath = event => {
  // 상세 경로는 일정 타입(mirae 포함)이 아닌 실제 게시판(board: edu/event) 기준
  const basePath = DETAIL_PATH_MAP[event.board];
  return basePath && event.postNo != null ? `/${basePath}/detail/${event.postNo}` : null;
};

const EVENT_ICON_MAP = {
  mirae: iconHouse,
  edu: iconBook,
  event: iconParty
};

// 필터 칩 노출 순서
const FILTER_TYPES = ['mirae', 'edu', 'event'];

// 일정 타입 정렬 우선순위 (배지·팝업 공통): 미래채움 → 교육 → 행사
const TYPE_ORDER = ['mirae', 'edu', 'event'];
const getTypeRank = type => {
  const i = TYPE_ORDER.indexOf(type);
  return i === -1 ? TYPE_ORDER.length : i;
};

const MAX_VISIBLE_BADGES = 2;

const formatDateKey = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ISO 문자열('2026-05-04T00:00:00+09:00')을 시간 정보 없는 로컬 자정 Date로 변환
const toDateOnly = isoStr => {
  if (!isoStr) return null;
  const [y, m, day] = String(isoStr).slice(0, 10).split('-').map(Number);
  if (!y || !m || !day) return null;
  return new Date(y, m - 1, day);
};

const formatPeriod = (start, end) => {
  const f = d => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  return `${f(start)} ~ ${f(end)}`;
};

// 게시글 목록을 일정 기간(startDate~endDate)에 따라 날짜별 이벤트로 펼친다
const buildEvents = posts => {
  const result = [];
  posts.forEach(post => {
    const board = BOARD_TYPE_MAP[post.boardType] || BOARD_TYPE_MAP[post.board]; // 'edu' | 'event'
    const start = toDateOnly(post.startDate);
    if (!board || !start) return;

    const end = toDateOnly(post.endDate) || start;
    if (end.getTime() < start.getTime()) return;

    // region이 '전체'(관리자 등록)인 글은 미래채움(mirae)으로 분류. 상세 경로는 board(교육/행사) 기준.
    const type = post.region === '전체' ? 'mirae' : board;
    const period = formatPeriod(start, end);
    const cursor = new Date(start);
    while (cursor.getTime() <= end.getTime()) {
      result.push({ date: formatDateKey(cursor), type, board, title: post.title, period, postNo: post.postNo });
      cursor.setDate(cursor.getDate() + 1);
    }
  });
  return result;
};

// react-calendar 그리드(6주, 일요일 시작)에 실제로 보이는 첫/끝 날짜 범위를 구한다.
// 전/다음 달의 일부 날짜도 표시되므로 해당 월이 아닌 그리드 전체 범위로 조회한다.
const getCalendarGridRange = activeStartDate => {
  const year = activeStartDate.getFullYear();
  const month = activeStartDate.getMonth();

  const monthStart = new Date(year, month, 1);
  const gridStart = new Date(monthStart);
  gridStart.setDate(monthStart.getDate() - monthStart.getDay()); // 그 주 일요일

  const monthEnd = new Date(year, month + 1, 0);
  const gridEnd = new Date(monthEnd);
  gridEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay())); // 그 주 토요일

  return { periodStart: formatDateKey(gridStart), periodEnd: formatDateKey(gridEnd) };
};

export default function Calendar({ region = '전체' }) {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';
  const [date, setDate] = useState(null);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [popupPosition, setPopupPosition] = useState(null);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false); // 조회가 300ms 넘게 걸릴 때만 스피너 노출 (빠른 응답 깜빡임 방지)
  const [activeType, setActiveType] = useState(null); // 일정 타입 필터 (null = 전체)
  const calendarRef = useRef(null);
  const isRegionSelected = region !== '전체';
  const isPopupOpen = selectedDateEvents.length > 0 && popupPosition;
  const regionCode = localList.find(local => local.name === region)?.code;
  // 표시 중인 달의 그리드(전/다음 달 포함) 범위 — 이 범위와 겹치는 일정만 조회한다.
  const { periodStart, periodEnd } = getCalendarGridRange(activeStartDate);

  // 선택 지역 + 표시 중인 달의 일정 데이터 조회 (교육정보 + 행사정보)
  useEffect(() => {
    // 지역/달 변경 시 열려있던 팝업/선택 상태 초기화
    setSelectedDateEvents([]);
    setPopupPosition(null);
    setDate(null);

    if (!isRegionSelected || !regionCode) {
      setEvents([]);
      setIsLoading(false);
      return;
    }

    let ignore = false;
    setIsLoading(true);
    const fetchEvents = async () => {
      try {
        const responses = await Promise.all(
          CALENDAR_BOARD_TYPES.map(boardType =>
            instance('/post/list', {
              method: 'POST',
              data: {
                boardType,
                region: regionCode,
                // 선택 지역 + 전체(관리자, region=전체) 게시물 함께 조회 → 전체 글은 미래채움으로 표시
                includeAllRegion: true,
                searchType: '',
                search: '',
                // 이벤트 기간(startDate~endDate)이 보이는 달 그리드와 겹치는 글만 조회 (백엔드 overlap 필터)
                periodStart,
                periodEnd,
                pageNo: 1,
                pageSize: 100,
                inOrder: 'DESC'
              }
            })
          )
        );
        if (ignore) return;
        const posts = responses.flatMap(res => (res.data?.status === 200 ? res.data.data.posts : []));
        setEvents(buildEvents(posts));
      } catch (error) {
        if (!ignore) {
          console.log(error);
          setEvents([]);
        }
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    fetchEvents();
    return () => {
      ignore = true;
    };
  }, [isRegionSelected, regionCode, periodStart, periodEnd]);

  // 로딩이 300ms 넘게 지속될 때만 스피너 노출 — 빠른 응답에선 깜빡임 없이 결과만 표시
  useEffect(() => {
    if (!isLoading) {
      setShowSpinner(false);
      return;
    }
    const timer = setTimeout(() => setShowSpinner(true), 300);
    return () => clearTimeout(timer);
  }, [isLoading]);

  // 날짜별 이벤트 조회용 맵 (타입 필터 적용)
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(event => {
      if (activeType && event.type !== activeType) return;
      (map[event.date] = map[event.date] || []).push(event);
    });
    // 미래채움 → 교육 → 행사 순 정렬 (같은 타입 내 기존 작성일 최신순은 유지)
    Object.values(map).forEach(list => list.sort((a, b) => getTypeRank(a.type) - getTypeRank(b.type)));
    return map;
  }, [events, activeType]);

  const getEventsForDate = dateKey => eventsByDate[dateKey] || [];

  useEffect(() => {
    if (isMobile && isPopupOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isPopupOpen]);

  const handlePrevMonth = () => {
    setActiveStartDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    handleClosePopup();
  };

  const handleNextMonth = () => {
    setActiveStartDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    handleClosePopup();
  };

  const handleDateClick = (val, e) => {
    if (!val || Array.isArray(val)) return;
    const dateKey = formatDateKey(val);
    const events = getEventsForDate(dateKey);

    if (events.length === 0) {
      handleClosePopup();
      setDate(null);
      return;
    }

    const isSameDate = date && date.getTime() === val.getTime();
    if (isSameDate) {
      handleClosePopup();
      setDate(null);
      return;
    }

    setDate(val);
    setSelectedDateEvents(events);

    // 클릭한 셀(button) 위치 기준으로 팝업 위치 계산
    const tile = e.currentTarget;
    const container = calendarRef.current;
    if (tile && container) {
      const containerRect = container.getBoundingClientRect();
      const tileRect = tile.getBoundingClientRect();
      const top = tileRect.bottom - containerRect.top + 8;
      let left = tileRect.left - containerRect.left;
      const popupWidth = 444;

      if (left + popupWidth > containerRect.width) {
        left = containerRect.width - popupWidth;
      }
      if (left < 0) left = 0;

      setPopupPosition({ top, left });
    }
  };

  const handleClosePopup = () => {
    setSelectedDateEvents([]);
    setPopupPosition(null);
    setDate(null);
  };

  // 필터 칩 클릭 — 같은 타입 다시 누르면 전체로 해제
  const handleFilterClick = type => {
    setActiveType(prev => (prev === type ? null : type));
    handleClosePopup();
  };

  const renderTileContent = ({ date: tileDate, view }) => {
    if (view !== 'month') return null;
    const events = getEventsForDate(formatDateKey(tileDate));
    if (events.length === 0) return null;

    // 배지는 타입당 1개씩만 (같은 타입 여러 건이어도 아이콘 1개), 미래채움 → 교육 → 행사 순
    const types = TYPE_ORDER.filter(type => events.some(e => e.type === type));
    const visible = types.slice(0, MAX_VISIBLE_BADGES);
    const hasMore = types.length > MAX_VISIBLE_BADGES;

    return (
      <div className={`event-badges count-${visible.length}${hasMore ? ' has-more' : ''}`}>
        <div className="badge-row">
          {visible.map(type => (
            <span key={type} className={`event-badge ${type}`}>
              <img src={EVENT_ICON_MAP[type]} alt="" />
            </span>
          ))}
        </div>
        {hasMore && (
          <div className="badge-more">
            {[0, 1, 2].map(i => (
              <span key={i} className="more-dot" />
            ))}
          </div>
        )}
      </div>
    );
  };

  // 이벤트를 타입별로 그룹핑 (미래채움 → 교육 → 행사)
  const groupEventsByType = events => {
    const groups = [];

    TYPE_ORDER.forEach(type => {
      const items = events.filter(e => e.type === type);
      if (items.length > 0) {
        groups.push({ type, items });
      }
    });

    return groups;
  };

  if (!isRegionSelected) {
    return (
      <Box className="calendar-component">
        <Flex className="calendar-empty" direction="column" align="center" justify="center">
          <img src={isMobile ? imgEmptyCalendarMo : imgEmptyCalendarPc} alt="" className="empty-image" />
          <Text as="p" className="title5 empty-title">
            왼쪽 지도에서 지역을 선택해 주세요.
          </Text>
          <Text as="p" className="body2 empty-description">
            센터별 일정을 한 눈에 확인할 수 있습니다.
          </Text>
        </Flex>
      </Box>
    );
  }

  const eventGroups = groupEventsByType(selectedDateEvents);

  return (
    <Box className="calendar-component" ref={calendarRef}>
      <Flex className="calendar-header" align="center" justify="between">
        <Flex align="center" gap="4" className="calendar-nav">
          <Text as="p" className="title5 region-name">
            {region}
          </Text>
          <Flex align="center" gap="3" className="month-nav">
            <button type="button" className="nav-btn" onClick={handlePrevMonth} aria-label="이전 달">
              <img src={iconArrowPrev} alt="" />
            </button>
            <Text as="p" className="title5 month-label">
              {activeStartDate.getFullYear()}년 {activeStartDate.getMonth() + 1}월
            </Text>
            <button type="button" className="nav-btn" onClick={handleNextMonth} aria-label="다음 달">
              <img src={iconArrowNext} alt="" />
            </button>
          </Flex>
        </Flex>
        <Flex className="filter-set">
          {FILTER_TYPES.map(type => {
            const isActive = activeType === type;
            return (
              <button
                key={type}
                type="button"
                className={`filter-btn ${type}${isActive ? ' active' : ''}`}
                onClick={() => handleFilterClick(type)}
                aria-pressed={isActive}
              >
                <img src={EVENT_ICON_MAP[type]} alt="" className="filter-icon" />
                <span className="filter-label">{EVENT_TYPE_LABEL[type]}</span>
              </button>
            );
          })}
        </Flex>
      </Flex>

      <div className="calendar-grid">
        <ReactCalendar
          value={date}
          onClickDay={handleDateClick}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({ activeStartDate: newDate }) => {
            if (newDate) setActiveStartDate(newDate);
          }}
          locale="ko-KR"
          calendarType="gregory"
          formatDay={formatDay}
          formatShortWeekday={formatShortWeekday}
          showNavigation={false}
          showNeighboringMonth={true}
          tileClassName={getTileClassName}
          tileContent={renderTileContent}
          minDetail="month"
        />

        {showSpinner && (
          <div className="calendar-loading" aria-live="polite">
            <span className="calendar-spinner" aria-hidden="true" />
            <span className="loading-text">일정을 불러오는 중…</span>
          </div>
        )}
      </div>

      {selectedDateEvents.length > 0 && popupPosition && (
        <>
          {isMobile && <div className="event-popup-dim" onClick={handleClosePopup} />}
          <div className="event-detail-popup" style={!isMobile ? { top: popupPosition.top, left: popupPosition.left } : undefined}>
            <button type="button" className="popup-close" onClick={handleClosePopup} aria-label="닫기">
              <img src={iconClose} alt="" />
            </button>
            <div className="popup-content">
              {eventGroups.map((group, gi) => (
                <div key={gi} className="event-group">
                  <div className="group-header">
                    <span className={`event-badge ${group.type}`}>
                      <img src={EVENT_ICON_MAP[group.type]} alt="" />
                    </span>
                    <span className="group-title">{EVENT_TYPE_LABEL[group.type]}</span>
                  </div>
                  <div className="group-items">
                    {group.items.map((item, ii) => {
                      const detailPath = getEventDetailPath(item);
                      return detailPath ? (
                        <Link key={ii} to={detailPath} className="event-item" title={`${item.title} 상세보기`} onClick={handleClosePopup}>
                          <p className="event-title">{item.title}</p>
                          <p className="event-period">{item.period}</p>
                        </Link>
                      ) : (
                        <div key={ii} className="event-item">
                          <p className="event-title">{item.title}</p>
                          <p className="event-period">{item.period}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Box>
  );
}
